/**
 * Turn-by-turn navigation along a registered Merustracker polyline (not road-network routing).
 */
import * as geolib from 'geolib';
import { haversineMeters as haversineLatLng } from './haversine';

export const RDP_EPSILON_M = 18;
export const MIN_TURN_DEG = 25;
export const SHARP_TURN_DEG = 70;
export const U_TURN_DEG = 140;
export const STOP_MATCH_M = 80;
export const OFF_ROUTE_M = 60;
export const SNAP_TO_LINE_M = 25;
export const STEP_ADVANCE_BUFFER_M = 20;

function toPoint(p) {
  const lat = Number(p && p.lat);
  const lng = Number(p && p.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

export function normalizeRoutePoints(customRoute) {
  if (!Array.isArray(customRoute)) return [];
  return customRoute.map(toPoint).filter(Boolean);
}

function haversineMeters(a, b) {
  const d = haversineLatLng(a.lat, a.lng, b.lat, b.lng);
  return Number.isFinite(d) ? d : 0;
}

function bearingDeg(a, b) {
  return geolib.getRhumbLineBearing(
    { latitude: a.lat, longitude: a.lng },
    { latitude: b.lat, longitude: b.lng },
  );
}

function smallestTurnAngleDeg(b1, b2) {
  let d = Math.abs(b2 - b1);
  if (d > 180) d = 360 - d;
  return d;
}

function planarXY(ll, refLatLng) {
  const R = 6371000;
  const cos = Math.cos((refLatLng.lat * Math.PI) / 180);
  const x = (((ll.lng - refLatLng.lng) * Math.PI) / 180) * R * cos;
  const y = (((ll.lat - refLatLng.lat) * Math.PI) / 180) * R;
  return { x, y };
}

function perpDistancePointToSegmentMeters(p, a, c) {
  const ref = { lat: (a.lat + c.lat) / 2, lng: (a.lng + c.lng) / 2 };
  const A = planarXY(a, ref);
  const P = planarXY(p, ref);
  const C = planarXY(c, ref);
  const vx = C.x - A.x;
  const vy = C.y - A.y;
  const wx = P.x - A.x;
  const wy = P.y - A.y;
  const len2 = vx * vx + vy * vy;
  if (len2 < 1e-4) return Math.hypot(wx, wy);
  let t = (wx * vx + wy * vy) / len2;
  t = Math.max(0, Math.min(1, t));
  const qx = A.x + t * vx;
  const qy = A.y + t * vy;
  return Math.hypot(P.x - qx, P.y - qy);
}

function projectOnSegment(p, a, c) {
  const ref = { lat: (a.lat + c.lat) / 2, lng: (a.lng + c.lng) / 2 };
  const A = planarXY(a, ref);
  const P = planarXY(p, ref);
  const C = planarXY(c, ref);
  const vx = C.x - A.x;
  const vy = C.y - A.y;
  const wx = P.x - A.x;
  const wy = P.y - A.y;
  const len2 = vx * vx + vy * vy;
  if (len2 < 1e-4) {
    return { t: 0, distM: Math.hypot(wx, wy), lat: a.lat, lng: a.lng };
  }
  let t = (wx * vx + wy * vy) / len2;
  t = Math.max(0, Math.min(1, t));
  const qx = A.x + t * vx;
  const qy = A.y + t * vy;
  const lat = ref.lat + ((qy / 6371000) * 180) / Math.PI;
  const lng =
    ref.lng + ((qx / (6371000 * Math.cos((ref.lat * Math.PI) / 180))) * 180) / Math.PI;
  return { t, distM: Math.hypot(P.x - qx, P.y - qy), lat, lng };
}

export function rdpSimplify(points, epsilonM) {
  if (!points || points.length < 3) return points ? points.slice() : [];
  const first = points[0];
  const last = points[points.length - 1];
  let maxD = 0;
  let idx = 0;
  for (let i = 1; i < points.length - 1; i += 1) {
    const d = perpDistancePointToSegmentMeters(points[i], first, last);
    if (d > maxD) {
      maxD = d;
      idx = i;
    }
  }
  if (maxD < epsilonM) return [first, last];
  const left = rdpSimplify(points.slice(0, idx + 1), epsilonM);
  const right = rdpSimplify(points.slice(idx), epsilonM);
  return left.slice(0, -1).concat(right);
}

function cumulativeDistances(polyline) {
  const cum = [0];
  for (let i = 1; i < polyline.length; i += 1) {
    cum.push(cum[i - 1] + haversineMeters(polyline[i - 1], polyline[i]));
  }
  return cum;
}

function distanceAtPolylineIndex(cum, index) {
  if (!cum.length) return 0;
  const i = Math.max(0, Math.min(index, cum.length - 1));
  return cum[i];
}

function classifyTurn(turnDeg, bearingBefore, bearingAfter) {
  let delta = bearingAfter - bearingBefore;
  while (delta > 180) delta -= 360;
  while (delta < -180) delta += 360;
  const right = delta > 0;

  if (turnDeg >= U_TURN_DEG) return right ? 'u-turn-right' : 'u-turn-left';
  if (turnDeg >= SHARP_TURN_DEG) return right ? 'sharp-right' : 'sharp-left';
  if (turnDeg >= MIN_TURN_DEG) return right ? 'turn-right' : 'turn-left';
  if (turnDeg >= 12) return right ? 'slight-right' : 'slight-left';
  return 'continue';
}

function nearestPolylineIndex(point, polyline) {
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < polyline.length; i += 1) {
    const d = haversineMeters(point, polyline[i]);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return { index: bestIdx, distanceM: bestDist };
}

function stopOrderValue(p) {
  const raw = p && (p.ordem_parada != null ? p.ordem_parada : p.cod_parada);
  const n = Number(raw);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

/**
 * @param {Array<{lat,lng}>} polylineInput
 * @param {Array<object>} stopsInput
 */
export function buildNavigationPlan(polylineInput, stopsInput) {
  let polyline = normalizeRoutePoints(polylineInput);
  let simplifiedRouteWarning = false;

  if (polyline.length < 2) {
    const stops = (Array.isArray(stopsInput) ? stopsInput : [])
      .slice()
      .sort((a, b) => stopOrderValue(a) - stopOrderValue(b));
    polyline = stops
      .map((s) => toPoint({ lat: s.lat_parada, lng: s.lng_parada }))
      .filter(Boolean);
    simplifiedRouteWarning = polyline.length >= 2;
  }

  if (polyline.length < 2) {
    return { polyline: polyline.slice(), instructions: [], simplifiedRouteWarning, totalLengthM: 0 };
  }

  const cum = cumulativeDistances(polyline);
  const totalLengthM = cum[cum.length - 1] || 0;
  const simplified = rdpSimplify(polyline, RDP_EPSILON_M);
  const maneuvers = [];

  for (let i = 1; i < simplified.length - 1; i += 1) {
    const prev = simplified[i - 1];
    const curr = simplified[i];
    const next = simplified[i + 1];
    const br1 = bearingDeg(prev, curr);
    const br2 = bearingDeg(curr, next);
    const turn = smallestTurnAngleDeg(br1, br2);
    if (turn < MIN_TURN_DEG) continue;

    const { index } = nearestPolylineIndex(curr, polyline);
    maneuvers.push({
      kind: 'turn',
      turnType: classifyTurn(turn, br1, br2),
      lat: curr.lat,
      lng: curr.lng,
      polylineIndex: index,
      distanceAlongRouteM: distanceAtPolylineIndex(cum, index),
      stopLabel: null,
    });
  }

  const stops = (Array.isArray(stopsInput) ? stopsInput : [])
    .slice()
    .sort((a, b) => stopOrderValue(a) - stopOrderValue(b));

  stops.forEach((s) => {
    const pt = toPoint({ lat: s.lat_parada, lng: s.lng_parada });
    if (!pt) return;
    const { index, distanceM } = nearestPolylineIndex(pt, polyline);
    if (distanceM > STOP_MATCH_M) return;

    const cod = s.cod_parada != null ? String(s.cod_parada) : '';
    const addr = s.endereco_parada ? String(s.endereco_parada) : '';
    const stopLabel = [cod, addr].filter((x) => x.trim()).join(' — ');

    maneuvers.push({
      kind: 'stop',
      turnType: null,
      lat: polyline[index].lat,
      lng: polyline[index].lng,
      polylineIndex: index,
      distanceAlongRouteM: distanceAtPolylineIndex(cum, index),
      stopLabel: stopLabel || cod || addr || null,
    });
  });

  maneuvers.sort((a, b) => {
    if (a.distanceAlongRouteM !== b.distanceAlongRouteM) {
      return a.distanceAlongRouteM - b.distanceAlongRouteM;
    }
    if (a.kind === 'stop' && b.kind !== 'stop') return 1;
    if (b.kind === 'stop' && a.kind !== 'stop') return -1;
    return 0;
  });

  const deduped = [];
  for (let j = 0; j < maneuvers.length; j += 1) {
    const m = maneuvers[j];
    const prev = deduped[deduped.length - 1];
    if (prev && Math.abs(prev.distanceAlongRouteM - m.distanceAlongRouteM) < 15) {
      if (m.kind === 'stop') deduped[deduped.length - 1] = m;
      continue;
    }
    deduped.push(m);
  }

  const instructions = [];

  instructions.push({
    kind: 'start',
    turnType: null,
    lat: polyline[0].lat,
    lng: polyline[0].lng,
    polylineIndex: 0,
    distanceAlongRouteM: 0,
    distanceToNextM: 0,
    stopLabel: null,
  });

  deduped.forEach((m) => {
    instructions.push({ ...m, distanceToNextM: 0 });
  });

  instructions.push({
    kind: 'arrive',
    turnType: null,
    lat: polyline[polyline.length - 1].lat,
    lng: polyline[polyline.length - 1].lng,
    polylineIndex: polyline.length - 1,
    distanceAlongRouteM: totalLengthM,
    distanceToNextM: 0,
    stopLabel: null,
  });

  for (let k = 0; k < instructions.length - 1; k += 1) {
    instructions[k].distanceToNextM = Math.max(
      0,
      instructions[k + 1].distanceAlongRouteM - instructions[k].distanceAlongRouteM,
    );
  }

  return { polyline, instructions, simplifiedRouteWarning, totalLengthM };
}

export function projectPointOnPolyline(point, polyline) {
  if (!point || !polyline || polyline.length < 2) {
    return {
      segmentIndex: 0,
      crossTrackDistanceM: Infinity,
      distanceAlongRouteM: 0,
      snappedLat: point ? point.lat : 0,
      snappedLng: point ? point.lng : 0,
    };
  }

  const cum = cumulativeDistances(polyline);
  let bestSeg = 0;
  let bestProj = null;
  let bestCross = Infinity;

  for (let i = 0; i < polyline.length - 1; i += 1) {
    const proj = projectOnSegment(point, polyline[i], polyline[i + 1]);
    if (proj.distM < bestCross) {
      bestCross = proj.distM;
      bestSeg = i;
      bestProj = proj;
    }
  }

  const segLen = haversineMeters(polyline[bestSeg], polyline[bestSeg + 1]);
  const along = cum[bestSeg] + (segLen > 0 ? bestProj.t * segLen : 0);

  return {
    segmentIndex: bestSeg,
    crossTrackDistanceM: bestCross,
    distanceAlongRouteM: along,
    snappedLat: bestProj.lat,
    snappedLng: bestProj.lng,
  };
}

export function findCurrentStepIndex(instructions, distanceAlongRouteM) {
  if (!instructions || !instructions.length) return 0;
  for (let i = 0; i < instructions.length; i += 1) {
    const step = instructions[i];
    if (step.kind === 'start') continue;
    if (step.distanceAlongRouteM > distanceAlongRouteM - STEP_ADVANCE_BUFFER_M) {
      return i;
    }
  }
  return instructions.length - 1;
}

export function getRemainingPolyline(polyline, distanceAlongRouteM) {
  if (!polyline || polyline.length < 2) return [];

  const cum = cumulativeDistances(polyline);
  const out = [];
  let started = false;

  for (let i = 0; i < polyline.length - 1; i += 1) {
    const segEnd = cum[i + 1];
    if (!started && segEnd <= distanceAlongRouteM) continue;
    if (!started) {
      const a = polyline[i];
      const b = polyline[i + 1];
      const segLen = segEnd - cum[i];
      const t =
        segLen > 0 ? Math.max(0, Math.min(1, (distanceAlongRouteM - cum[i]) / segLen)) : 0;
      const ref = { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 };
      const A = planarXY(a, ref);
      const C = planarXY(b, ref);
      const qx = A.x + t * (C.x - A.x);
      const qy = A.y + t * (C.y - A.y);
      const lat = ref.lat + ((qy / 6371000) * 180) / Math.PI;
      const lng =
        ref.lng + ((qx / (6371000 * Math.cos((ref.lat * Math.PI) / 180))) * 180) / Math.PI;
      out.push({ lat, lng });
      started = true;
    }
    if (started) out.push(polyline[i + 1]);
  }

  if (!out.length && polyline.length) {
    return [polyline[polyline.length - 1]];
  }
  return out;
}

export function formatDistanceMeters(meters, kmLabel, mLabel) {
  const m = Number(meters);
  if (!Number.isFinite(m) || m <= 0) return '';
  if (m >= 1000) return `${(m / 1000).toFixed(1)} ${kmLabel || 'km'}`;
  return `${Math.round(m)} ${mLabel || 'm'}`;
}

export function maneuverIcon(turnType, kind) {
  if (kind === 'stop') return 'mdi-bus-stop';
  if (kind === 'arrive') return 'mdi-flag-checkered';
  if (kind === 'start') return 'mdi-play';
  switch (turnType) {
    case 'u-turn-left':
    case 'u-turn-right':
      return 'mdi-arrow-u-left-top';
    case 'sharp-left':
    case 'turn-left':
    case 'slight-left':
      return 'mdi-arrow-left-bottom';
    case 'sharp-right':
    case 'turn-right':
    case 'slight-right':
      return 'mdi-arrow-right-bottom';
    default:
      return 'mdi-arrow-up-bold';
  }
}
